import React, { useRef } from "react";
import { ListViewComponent } from "@syncfusion/ej2-react-lists";

import { IBoltComment } from "../../types/IBoltComment";
import { CommentsListItem } from "./CommentsListItem";
import {
  CommentsODataHookProps,
  useCommentsOData,
} from "../../hooks/useCommentsOData";
import { useCommentsList } from "./useCommentsList";
import {
  ContentMode as ContentEditorContentMode,
  InlineCommentEditor,
} from "../CommentEditor/InlineCommentEditor";
import { utils } from "@ucl/library";

export interface CommentsListProps extends CommentsODataHookProps {
  canEdit?: boolean;
  canPin?: boolean;
  canComment?: boolean;
  canDelete?: boolean;
  onEdit?: (comment: IBoltComment) => Promise<void>;
  onTogglePin?: (comment: IBoltComment) => Promise<void>;
  onDelete?: (comment: IBoltComment) => Promise<void>;
  onCommentCreated?: (comment: IBoltComment) => Promise<void>;
  className?: string;
  createCommentContentMode?: ContentEditorContentMode;
  showHeader?: boolean;
}

export const CommentsList: React.FC<CommentsListProps> = ({
  getAuthRequestHeader,
  apiBaseUrl,
  entityType,
  entityId,
  canEdit = false,
  canPin = false,
  canComment = false,
  canDelete = false,
  onEdit,
  onTogglePin,
  onDelete,
  onCommentCreated,
  className = "",
  createCommentContentMode = "textOnly",
  odataPath = "odata/boltcommentodata",
  showHeader = true,
}) => {
  const listViewRef = useRef<ListViewComponent>(null);
  const { commentsDataManager, commentsQuery } = useCommentsOData({
    getAuthRequestHeader,
    apiBaseUrl,
    entityType,
    entityId,
    odataPath,
  });

  const { handleEdit, handleTogglePin, handleDelete, refreshComments } =
    useCommentsList({
      listViewRef,
      onEdit,
      onTogglePin,
      onDelete,
    });

  const handleCommentCreated = async (comment: IBoltComment) => {
    await refreshComments();
    if (onCommentCreated) {
      await onCommentCreated(comment);
    }
  };

  const actionCompleteHandler = () => {
    utils.miscellaneous.scrollToEnd(
      listViewRef.current?.element as HTMLElement
    );
  };

  return (
    <section className={`bolt-comments-list ${className}`}>
      {showHeader && (
        <div className="bolt-comments-header">
          <h3>Comments</h3>
        </div>
      )}
      <div className="bolt-comments-content">
        {commentsDataManager && (
          <ListViewComponent
            dataSource={commentsDataManager}
            template={(comment: IBoltComment) => (
              <CommentsListItem
                comment={comment}
                canEdit={canEdit}
                canTogglePin={canPin}
                canDelete={canDelete}
                onEdit={handleEdit}
                onTogglePin={handleTogglePin}
                onDelete={handleDelete}
              />
            )}
            actionComplete={actionCompleteHandler}
            showHeader={false}
            cssClass="bolt-comments-listview"
            query={commentsQuery}
            ref={listViewRef}
          />
        )}
        {/* {listViewRef.current && !listViewRef.current.localData && (
          <div className="bolt-comments-empty">
            <div className="empty-icon">💬</div>
            <div className="empty-title">No comments yet</div>
            <div className="empty-description">Make the first comment.</div>
          </div>
        )} */}
        {canComment && (
          <div className="bolt-comments-new-comment">
            <InlineCommentEditor
              entityType={entityType}
              entityId={entityId}
              onCommentCreated={handleCommentCreated}
              contentMode={createCommentContentMode}
            />
          </div>
        )}
      </div>
      <div className="bolt-comments-footer-block"></div>
    </section>
  );
};
